import type {taxonomy_editor} from "../TaxonomyEditorTypes";
import {IS_TE_DEVELOPMENT_MODE} from "../TaxonomyEditorOptions";
import type {HierarchyNode, HierarchyRectangularNode} from "d3";

type Node = taxonomy_editor.GraphNode;
type AnyNode = HierarchyNode<taxonomy_editor.NodeData> | HierarchyRectangularNode<taxonomy_editor.NodeData> | Node;

export const ALL_TAXONOMY_VALUE_MODES = [
    {
        key: 'n_categories' as const,
        calc: 'leaf=1' as const,
        initCalcFromLeaf: true as const,
        label: 'Number of categories',
    },
    {
        key: 'p__spend__sum' as const,
        calc: 'avg_dist' as const,
        initCalcFromLeaf: true as const,
        label: 'Total spend',
    },
    {
        key: 'equal' as const,
        calc: 'equal' as const,
        initCalcFromLeaf: false as const,
        label: 'Equal',
    },
]

// These are in use
export const TAXONOMY_VALUE_MODES = [ALL_TAXONOMY_VALUE_MODES[0], ALL_TAXONOMY_VALUE_MODES[2]]

/**
 * Value mode in which all edit functionalities are supported
 */
export const EDIT_VALUE_MODE = 'n_categories' as const

export type TaxonomyNodeValueMode = typeof ALL_TAXONOMY_VALUE_MODES[number]

/**
 * Re-calculates the values for the whole tree based
 *
 *  TODO: We can optimize the calculation by not recalculating the whole tree
 *    However this was a premature optimization that did not properly remove the values when moving nodes around
 * @param valueMode
 * @param changed
 * @param changed2
 */
export function assignValues(valueMode: TaxonomyNodeValueMode, changed: AnyNode, ...changed2: AnyNode[]) {
    if (IS_TE_DEVELOPMENT_MODE) {
        console.log('[TaxonomyTreeValueModes.assignValues]')
        console.time('[TaxonomyTreeValueModes.assignValues]')
    }


    const root = changed.ancestors().at(-1);
    if (!root) return;

    if (IS_TE_DEVELOPMENT_MODE) {
        root.eachBefore(n => {
            console.log('[TaxonomyTreeValueModes.assignValues] before', {
                id: n.data.id,
                l: n.data.label,
                val: n.value,
                children_n: n.children?.length
            })
        })
    }

    const calc = valueMode.calc
    if (valueMode.initCalcFromLeaf) {
        const key = valueMode.key;
        root
            .sum(d => {
                if (d.removed) return 0;

                switch (calc) {
                    case 'leaf=1': {
                        const nChildren = (d.children ?? []).filter(d => !d.removed).length;
                        return nChildren === 0 ? 1 : 0;
                    }
                    case 'avg_dist':
                        if (key in d.values) {
                            return d.values[key] || 0
                        } else {
                            console.warn(`Could not find ${key} in values`,
                                '' + Object.keys(d.values))
                            return 0
                        }
                }
            })
            .sort((a, b) =>
                // b.height - a.height ||
                a.value && b.value ? b.value - a.value : 0
            )
    } else {
        switch (calc) {
            case 'equal':
                root.eachBefore(n => {
                    if (n.parent) {
                        const parentValue = n.parent.data.values['equal'] || 1;
                        // const parentValue = 1000;
                        n.data.values['equal'] = parentValue / (n.parent.children?.length || 1);
                    } else {
                        n.data.values['equal'] = 1000;
                    }
                }).sum(d => {
                    if (d.removed) return 0;
                    return (d.children.length === 0 ? d.values['equal'] : 0) || 0;
                })
                break;
            default:
                throw Error('Not implemented yet...')
        }
    }
    if (IS_TE_DEVELOPMENT_MODE) {
        root.eachBefore(n => {
            console.log('[TaxonomyTreeValueModes.assignValues] after', {id: n.data.id, l: n.data.label, val: n.value})
        })
        console.timeEnd('[TaxonomyTreeValueModes.assignValues]')
    }
}
